import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { actions } from '../../state';
import { isEmpty } from '../../utils';
import { adminPermissionDefinitions } from './admins.data';
import Form from './admins.form';
import AdminRecords from './admins.records';
import { Paper, PaperHeader, TitleSecondary, Toggle } from '../../components';
import {
  buildAdminPayload,
  getDefaultAdminValues,
  isPermissionSelected,
  mapAdminDetailsToState,
  setAllPermissionsSelection,
  togglePermissionSelection,
} from './admins.helpers';

const ACTIVITY_PAGE_SIZE = 10;
const locationPermission = adminPermissionDefinitions.find(
  ({ key }) => key === 'locations'
);

const EditAdmin = ({
  dispatch,
  history,
  match: { params },
  admins: {
    loading,
    selectedAdmin,
    assignableLocations,
    assignableLocationsLoading,
    activityRecords,
    activityRecordsLoading,
    activityRecordsTotal,
  },
}) => {
  const [permissions, setPermissions] = useState([]);
  const [values, setValues] = useState(getDefaultAdminValues());
  const [status, setStatus] = useState(false);
  const [locationIds, setLocationIds] = useState([]);
  const [allLocationsSelected, setAllLocationsSelected] = useState(false);
  const [
    locationEmailNotificationEnabled,
    setLocationEmailNotificationEnabled,
  ] = useState(false);
  const [
    locationSmsNotificationEnabled,
    setLocationSmsNotificationEnabled,
  ] = useState(false);
  const [recordsPage, setRecordsPage] = useState(1);

  const fetchAdmin = useCallback(
    id => {
      dispatch(actions.admins.getAdminAction(id));
    },
    [dispatch]
  );

  const fetchAssignableLocations = useCallback(() => {
    dispatch(actions.admins.getAssignableLocationsAction());
  }, [dispatch]);

  const fetchActivityRecords = useCallback(
    (id, page) => {
      dispatch(
        actions.admins.getActivityRecordsAction(id, {
          offset: (page - 1) * ACTIVITY_PAGE_SIZE,
          limit: ACTIVITY_PAGE_SIZE,
        })
      );
    },
    [dispatch]
  );

  const loadValues = useCallback(() => {
    if (!isEmpty(selectedAdmin)) {
      const adminState = mapAdminDetailsToState(selectedAdmin);

      setValues(adminState.values);
      setPermissions(adminState.permissions);
      setStatus(adminState.status);
      setLocationIds(adminState.locationIds);
      setAllLocationsSelected(adminState.allLocationsSelected);
      setLocationEmailNotificationEnabled(
        adminState.locationEmailNotificationEnabled
      );
      setLocationSmsNotificationEnabled(
        adminState.locationSmsNotificationEnabled
      );
    }
  }, [selectedAdmin]);

  const clearAdmins = useCallback(() => {
    dispatch(actions.admins.clearAdminsAction());
  }, [dispatch]);

  useEffect(() => {
    fetchAdmin(params.id);
    fetchAssignableLocations();
    return () => clearAdmins();
  }, [fetchAdmin, fetchAssignableLocations, params.id, clearAdmins]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  useEffect(() => {
    fetchActivityRecords(params.id, recordsPage);
  }, [fetchActivityRecords, params.id, recordsPage]);

  const handleInputChange = ({ target: { name, value } }) => {
    const updatedValue = name === 'business_role' ? Number(value) : value;
    setValues({ ...values, [name]: updatedValue });
  };

  const clearLocationAccess = () => {
    setLocationIds([]);
    setAllLocationsSelected(false);
    setLocationEmailNotificationEnabled(false);
    setLocationSmsNotificationEnabled(false);
  };

  const handlePermissionChange = (permission, checked) => {
    const nextPermissions = togglePermissionSelection(
      permissions,
      permission,
      checked
    );

    setPermissions(nextPermissions);

    if (permission.supportsLocationScope && !checked) {
      clearLocationAccess();
    }
  };

  const handleSelectAllPermissions = ({ target: { checked } }) => {
    setPermissions(
      setAllPermissionsSelection(adminPermissionDefinitions, checked)
    );

    if (!checked) {
      clearLocationAccess();
      return;
    }

    setLocationEmailNotificationEnabled(true);
    setLocationSmsNotificationEnabled(true);
  };

  const handleLocationIdsChange = nextLocationIds => {
    const shouldSelectAll =
      !nextLocationIds.length ||
      nextLocationIds.length === assignableLocations.length;

    setAllLocationsSelected(shouldSelectAll);
    setLocationIds(shouldSelectAll ? [] : nextLocationIds);
  };

  const hasLocationsPermission = locationPermission
    ? isPermissionSelected(permissions, locationPermission)
    : false;

  const handleSubmit = e => {
    e.preventDefault();
    const body = {
      ...buildAdminPayload({
        values,
        permissions,
        locationIds,
        allLocationsSelected,
        locationEmailNotificationEnabled,
        locationSmsNotificationEnabled,
        hasLocationsPermission,
      }),
      password: values.password.length ? values.password : null,
    };

    dispatch(actions.admins.editAdminAction(params.id, body, history));
  };

  const toggleStatus = () => {
    const newStatus = status ? 0 : 1;
    setStatus(status => !status);

    dispatch(actions.admins.deactivateAdminAction(params.id, newStatus));
  };

  return (
    <Paper size="lg">
      <PaperHeader>
        <TitleSecondary>Redaguoti administratoriaus paskyrą</TitleSecondary>
        <Toggle
          onChange={toggleStatus}
          checked={status}
          showMeta={true}
          onText="Aktyvus"
          offText="Deaktyvuotas"
        />
      </PaperHeader>

      <Form
        type="edit"
        values={values}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        loading={loading}
        permissionDefinitions={adminPermissionDefinitions}
        selectedPermissions={permissions}
        onPermissionChange={handlePermissionChange}
        onSelectAllPermissions={handleSelectAllPermissions}
        assignableLocations={assignableLocations}
        assignableLocationsLoading={assignableLocationsLoading}
        selectedLocationIds={locationIds}
        allLocationsSelected={allLocationsSelected}
        onLocationIdsChange={handleLocationIdsChange}
        locationEmailNotificationEnabled={locationEmailNotificationEnabled}
        onLocationNotificationChange={({ target: { checked } }) =>
          setLocationEmailNotificationEnabled(checked)
        }
        locationSmsNotificationEnabled={locationSmsNotificationEnabled}
        onLocationSmsNotificationChange={({ target: { checked } }) =>
          setLocationSmsNotificationEnabled(checked)
        }
      />
      <AdminRecords
        admin={selectedAdmin}
        records={activityRecords}
        loading={activityRecordsLoading}
        total={activityRecordsTotal}
        page={recordsPage}
        onPageChange={setRecordsPage}
      />
    </Paper>
  );
};

const mapStateToProps = state => ({
  admins: state.admins,
});

EditAdmin.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  admins: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    selectedAdmin: PropTypes.object.isRequired,
    assignableLocations: PropTypes.array.isRequired,
    assignableLocationsLoading: PropTypes.bool.isRequired,
    activityRecords: PropTypes.array.isRequired,
    activityRecordsLoading: PropTypes.bool.isRequired,
    activityRecordsTotal: PropTypes.number.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(EditAdmin);
